import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Database, Download, Upload, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { backupService } from '@/services/backup.service';
import { getErrorMessage } from '@/services';

export default function AdminBackup() {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: boolean;
    message: string;
    errors?: string[];
  } | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await backupService.exportAdmin();
      const filename = `dazn-panel-full-backup-${new Date().toISOString().split('T')[0]}.json`;
      backupService.downloadBlob(blob, filename);
      toast.success(t('backup.exportSuccess'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const confirmMessage = t('backup.importConfirmAdmin');
      if (!window.confirm(confirmMessage)) {
        return;
      }

      setIsImporting(true);
      setImportResult(null);

      try {
        const result = await backupService.importAdmin(file);
        setImportResult(result);
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error) || t('common.error');
        setImportResult({
          success: false,
          message: errorMessage,
        });
        toast.error(errorMessage);
      } finally {
        setIsImporting(false);
      }
    };
    input.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-dark-100">{t('backup.title')}</h1>
        <p className="text-dark-400 mt-1">{t('backup.subtitle')}</p>
      </div>

      {/* Export Card */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
            <Download className="text-primary-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-100">{t('backup.exportTitle')}</h2>
            <p className="text-dark-400 text-sm">{t('backup.exportDescription')}</p>
          </div>
        </div>

        <div className="bg-dark-800 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-medium text-dark-200 mb-2">{t('backup.exportIncludes')}</h3>
          <ul className="text-sm text-dark-400 space-y-1">
            <li>• {t('backup.includesUsers')}</li>
            <li>• {t('backup.includesPackages')}</li>
            <li>• {t('backup.includesDazn')}</li>
            <li>• {t('backup.includesProxies')}</li>
            <li>• {t('backup.includesXaccel')}</li>
            <li>• {t('backup.includesSports')}</li>
            <li>• {t('backup.includesSchedule')}</li>
            <li>• {t('backup.includesSettings')}</li>
          </ul>
        </div>

        <button
          onClick={handleExport}
          disabled={isExporting}
          className="btn-primary"
        >
          {isExporting ? (
            <Loader2 size={18} className="animate-spin mr-2" />
          ) : (
            <Download size={18} className="mr-2" />
          )}
          {isExporting ? t('backup.exporting') : t('backup.exportButton')}
        </button>
      </div>

      {/* Import Card */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-warning-50 flex items-center justify-center">
            <Upload className="text-warning-600" size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-100">{t('backup.importTitle')}</h2>
            <p className="text-dark-400 text-sm">{t('backup.importDescription')}</p>
          </div>
        </div>

        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-warning-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h3 className="text-sm font-medium text-warning-800">{t('backup.importWarningTitle')}</h3>
              <p className="text-sm text-warning-700 mt-1">{t('backup.importWarningText')}</p>
            </div>
          </div>
        </div>

        <button
          onClick={handleImport}
          disabled={isImporting}
          className="btn-secondary"
        >
          {isImporting ? (
            <Loader2 size={18} className="animate-spin mr-2" />
          ) : (
            <Upload size={18} className="mr-2" />
          )}
          {isImporting ? t('backup.importing') : t('backup.importButton')}
        </button>

        {/* Import Result */}
        {importResult && (
          <div className={`mt-4 p-4 rounded-lg ${importResult.success ? 'bg-success-50 border border-success-200' : 'bg-error-50 border border-error-200'}`}>
            <div className="flex items-start gap-3">
              {importResult.success ? (
                <CheckCircle className="text-success-600 flex-shrink-0 mt-0.5" size={20} />
              ) : (
                <AlertTriangle className="text-error-600 flex-shrink-0 mt-0.5" size={20} />
              )}
              <div>
                <p className={`text-sm font-medium ${importResult.success ? 'text-success-800' : 'text-error-800'}`}>
                  {importResult.message}
                </p>
                {importResult.errors && importResult.errors.length > 0 && (
                  <ul className="mt-2 text-sm text-error-700 space-y-1">
                    {importResult.errors.map((err, i) => (
                      <li key={i}>• {err}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="card bg-dark-800">
        <div className="flex items-center gap-3">
          <Database className="text-dark-400" size={20} />
          <p className="text-sm text-dark-400">
            {t('backup.infoText')}
          </p>
        </div>
      </div>
    </div>
  );
}
